/**
 * Team chat + access requests. Talks to the /chat group (one table, mc_chat).
 *
 * Realtime is a poll: /chat/sync?since=<last id> every 4 s while the drawer is
 * open on the Chat tab, every 30 s otherwise (the badge and presence still need
 * it). Each poll also stamps the person as "online". Non-namespaced like every
 * other module here.
 *
 * END-TO-END ENCRYPTION (the e2ee* actions; the maths is in chatCrypto.js): when the
 * server has mc_users.chat_pubkey, every channel except the assistant's direct message
 * is encrypted in the browser. Keys never sit in the reactive state: the private key
 * and the channel keys live in `vault` below and in IndexedDB. A row's decrypted text
 * is added to the message as `text`; a row this browser cannot open gets `locked`.
 */
import * as cc from "./chatCrypto";

const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Chat", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    p.append(k, Array.isArray(v) || typeof v === "object" ? JSON.stringify(v) : v);
  });
  return p;
};

// Drawer open on Chat: 4 s. Tab visible: 10 s (a request or a message shows up
// within that). Tab hidden: 60 s. Coming back to the tab polls at once.
const FAST_MS = 4000;
const VISIBLE_MS = 10000;
const HIDDEN_MS = 60000;
const CHANNELS_REFRESH_MS = 300000;   // membership (who to wrap keys for) is re-read this often
const KEYS_RETRY_MS = 20000;          // a message under a key we lack asks the server again at most this often
const PLAIN_MAX = 3000;               // ciphertext is ~1.4x larger; the server caps the wire at 12,000

/** Key material, deliberately outside the store (never reactive, never serialised). */
const vault = {
  uid: 0,
  priv: null,          // my ECDH private key (CryptoKey)
  keys: {},            // channel -> { ver -> { key, raw } }
  coverage: [],        // [{ channel, ver, uid, fp }] who holds which version (from the server)
  seen: {},            // wrapped-key row ids already unwrapped or skipped
  restored: false,
  loading: null,
  filling: false,
  lastKeyFetch: 0,
};
const resetVault = () => Object.assign(vault, { uid: 0, priv: null, keys: {}, coverage: [], seen: {}, restored: false, loading: null, filling: false, lastKeyFetch: 0 });
const e2eeChannel = (s, key) => !!(s.e2ee.enabled && key && key.indexOf("dm:0:") !== 0);
const newestVer = (channel) => Math.max(0, ...Object.keys(vault.keys[channel] || {}).map(Number));
async function holdKey(channel, ver, raw, persist) {
  const key = await cc.importChannelKey(raw);
  if (!vault.keys[channel]) vault.keys[channel] = {};
  vault.keys[channel][ver] = { key, raw };
  if (persist) { try { await cc.saveChannelKey(vault.uid, channel, ver, raw); } catch (e) { /* memory only, then */ } }
}
/** Public key + fingerprint of a member (me included), or null while they have none. */
function pubOf(s, uid) {
  if (s.me && uid === s.me.uid) return s.e2ee.pubkey ? { pubkey: s.e2ee.pubkey, fp: s.e2ee.fp } : null;
  const p = s.people.find((x) => x.uid === uid);
  return p && p.pubkey ? { pubkey: p.pubkey, fp: p.fp } : null;
}
/** Who may open a channel (uids), from the server's list; a direct message is its two people. */
function membersOf(s, channel) {
  const c = s.channels.find((x) => x.key === channel);
  if (c && Array.isArray(c.member_uids)) return c.member_uids;
  const m = /^dm:(\d+):(\d+)$/.exec(channel || "");
  if (m) return [Number(m[1]), Number(m[2])].filter((u) => u > 0);
  return null;
}
async function wrapRowsFor(s, channel, raw, uids) {
  const rows = [];
  for (const uid of uids) {
    const p = pubOf(s, uid);
    if (p) rows.push(uid + ":" + p.fp + ":" + (await cc.wrapFor(p.pubkey, raw)));
  }
  return rows;
}
function markCovered(channel, ver, rows) {
  rows.forEach((r) => { const p = r.split(":"); vault.coverage.push({ channel, ver, uid: Number(p[0]), fp: p[1] }); });
}

const state = () => ({
  loaded: false,
  error: "",
  me: null, // { uid, name, role, moderator, approver, can_manage_groups }
  channels: [],
  people: [],
  roles: [],
  active: "", // channel key
  messages: {}, // key -> [message]
  hasMore: {}, // key -> bool
  loadedKeys: {}, // key -> true once history was fetched
  lastId: 0,
  online: [],
  totalUsers: 0,
  timer: null,
  inflight: false,
  initPromise: null,
  channelsAt: 0,
  // off = server without the column | setup = no key yet | unlock = key exists elsewhere |
  // ready | nocrypto = this browser cannot do it
  e2ee: { supported: cc.supported(), enabled: false, status: "off", fp: "", pubkey: "", hasBackup: false, busy: false, error: "" },
});

const mutations = {
  SET_CHAT_CHANNELS(s, d) {
    s.me = d.me;
    s.channels = d.channels || [];
    s.people = d.people || [];
    s.roles = d.roles || [];
    if (!s.lastId) s.lastId = d.last_id || 0;
    s.loaded = true;
    s.error = "";
    s.channelsAt = Date.now();
    s.e2ee.enabled = !!d.e2ee;
  },
  SET_E2EE(s, patch) { Object.assign(s.e2ee, patch); },
  /** Decrypted text (or a lock) for rows this browser worked on. */
  CHAT_SET_TEXTS(s, list) {
    list.forEach((t) => {
      const m = (s.messages[t.channel] || []).find((x) => x.id === t.id);
      if (m) { m.text = t.text; m.locked = t.locked; }
    });
  },
  SET_CHAT_ERROR(s, m) { s.error = m; s.loaded = true; },
  SET_CHAT_ACTIVE(s, key) { s.active = key; },
  SET_CHAT_HISTORY(s, { key, list, hasMore, prepend }) {
    const cur = s.messages[key] || [];
    // Not prepending = a fresh page; keep anything newer that a poll appended while it loaded.
    const lastId = list.length ? list[list.length - 1].id : 0;
    const merged = prepend ? list.concat(cur) : list.concat(cur.filter((m) => m.id > lastId));
    s.messages[key] = merged;
    s.hasMore[key] = !!hasMore;
    s.loadedKeys[key] = true;
  },
  /** shown = the active channel is really on screen (drawer open on Chat/Assistant, tab visible). */
  APPEND_CHAT_MESSAGES(s, { list, shown }) {
    list.forEach((m) => {
      const cur = s.messages[m.channel] || [];
      if (cur.some((x) => x.id === m.id)) return;
      s.messages[m.channel] = cur.concat([m]);
      if (!(shown && m.channel === s.active) && s.me && m.from_uid !== s.me.uid) {
        const ch = s.channels.find((c) => c.key === m.channel);
        if (ch) ch.unread = (ch.unread || 0) + 1;
        else if (m.channel.indexOf("dm:") === 0) {
          // A direct message from someone new: show the thread right away.
          const p = m.channel.split(":");
          const other = Number(p[1]) === s.me.uid ? Number(p[2]) : Number(p[1]);
          s.channels.push({ key: m.channel, kind: "dm", name: m.from_name, other_uid: other, unread: 1, last_read_id: 0 });
        }
      }
      if (m.id > s.lastId) s.lastId = m.id;
    });
  },
  SET_CHAT_SYNC(s, { last_id, online, total_users }) {
    if (last_id > s.lastId) s.lastId = last_id;
    const next = online || [];
    if (next.join(",") !== s.online.join(",")) s.online = next;   // unchanged presence must not re-render everything
    if ((total_users || 0) !== s.totalUsers) s.totalUsers = total_users || 0;
  },
  /** Rows that changed in place on the server (deleted, decided, a card run): patch the copies we hold. */
  APPLY_CHAT_CHANGES(s, list) {
    list.forEach((c) => {
      const arr = s.messages[c.channel];
      if (!arr) return;
      const m = arr.find((x) => x.id === c.id);
      if (m) Object.assign(m, { body: c.body, deleted: c.deleted, req_status: c.req_status, resolved_by: c.resolved_by, resolved_by_name: c.resolved_by_name, resolved_at: c.resolved_at });
    });
  },
  CHAT_READ_LOCAL(s, key) {
    const ch = s.channels.find((c) => c.key === key);
    if (ch) ch.unread = 0;
  },
  SET_CHAT_TIMER(s, t) { s.timer = t; },
  SET_CHAT_INFLIGHT(s, v) { s.inflight = v; },
  /** A field of one message changed on the server (a proposal card's status). */
  PATCH_CHAT_MESSAGE(s, { id, patch }) {
    Object.keys(s.messages).forEach((k) => {
      const m = (s.messages[k] || []).find((x) => x.id === id);
      if (m) Object.assign(m, patch);
    });
  },
  RESET_CHAT(s) { if (s.timer) clearTimeout(s.timer); resetVault(); Object.assign(s, state(), { timer: null }); },
  /** Empty one channel's messages here (after the server cleared them). */
  CLEAR_CHAT_CHANNEL(s, key) {
    s.messages[key] = [];
    s.hasMore[key] = false;
    s.loadedKeys[key] = true;
    const ch = s.channels.find((c) => c.key === key);
    if (ch) ch.unread = 0;
  },
  CHAT_MESSAGE_DELETED(s, id) {
    Object.keys(s.messages).forEach((k) => {
      const m = s.messages[k].find((x) => x.id === id);
      if (m) { m.deleted = true; m.body = ""; }
    });
  },
};

const getters = {
  chatUnread: (s) => s.channels.reduce((a, c) => a + (c.unread || 0), 0),
  chatPresence: (s) => (s.totalUsers ? s.online.length + " of " + s.totalUsers + " online" : ""),
  chatOnline: (s) => (uid) => s.online.indexOf(uid) !== -1,
  /** Is this channel encrypted here (the assistant's direct message never is)? */
  chatEncrypted: (s) => (key) => e2eeChannel(s, key),
  /** The recent conversation, decrypted, as the assistant's mention endpoint wants it: "uid|name|text" per line. */
  chatContext: (s) => (channel, excludeId) => (s.messages[channel] || [])
    .filter((m) => m.kind === "text" && !m.deleted && !m.locked && m.id !== excludeId)
    .slice(-12)
    .map((m) => m.from_uid + "|" + String(m.from_name || "").replace(/[|\n]/g, " ") + "|" + String(m.text !== undefined && m.text !== null ? m.text : m.body).replace(/\s*\n\s*/g, " "))
    .join("\n"),
};

const actions = {
  /** Once after sign-in: channels, approvals, and the poll. Quiet on an API without chat. */
  async chatInit({ commit, dispatch, state: s }) {
    if (s.loaded || s.initPromise) return s.initPromise;   // once; a poll that fired first does not count as "initialised"
    s.initPromise = (async () => {
      try {
        const { data } = await window.$http.axios.get("chat/channels", { silent: true });
        if (data.status) commit("SET_CHAT_CHANNELS", data);
        else { commit("SET_CHAT_ERROR", data.message || "The server refused the request."); return; }
      } catch (e) {
        commit("SET_CHAT_ERROR", "Chat is not installed on this server yet.");
        return;
      } finally { s.initPromise = null; }
      dispatch("chatApprovals");
      dispatch("chatSchedule");
      dispatch("e2eeInit");
    })();
    await s.initPromise;
    if (!window.__chatWake) {
      window.__chatWake = () => { if (document.visibilityState === "visible") dispatch("chatPoll"); };
      document.addEventListener("visibilitychange", window.__chatWake);
      window.addEventListener("focus", window.__chatWake);
    }
  },

  chatSchedule({ commit, dispatch, state: s, rootState }) {
    if (s.timer) clearTimeout(s.timer);
    const visible = document.visibilityState === "visible";
    const fast = visible && rootState.panel.drawerOpen && (rootState.panel.drawerTab === "chat" || rootState.panel.drawerTab === "assistant");
    const t = setTimeout(() => dispatch("chatPoll"), fast ? FAST_MS : visible ? VISIBLE_MS : HIDDEN_MS);
    commit("SET_CHAT_TIMER", t);
  },

  async chatPoll({ commit, dispatch, state: s, rootState }) {
    if (!localStorage.getItem("token")) { commit("RESET_CHAT"); return; }
    if (s.inflight) return;   // one poll at a time; the schedule after it covers the rest
    commit("SET_CHAT_INFLIGHT", true);
    try {
      const { data } = await window.$http.axios.get("chat/sync", { params: { since: s.lastId }, silent: true });
      if (data.status) {
        const shown = rootState.panel.drawerOpen && (rootState.panel.drawerTab === "chat" || rootState.panel.drawerTab === "assistant") && document.visibilityState === "visible";
        if (data.keys && data.keys.length) {
          await dispatch("e2eeAbsorbKeyRows", data.keys);   // keys first, so the messages below open at once
          dispatch("e2eeDecryptAll");
        }
        if (data.messages && data.messages.length) {
          commit("APPEND_CHAT_MESSAGES", { list: data.messages, shown });
          dispatch("e2eeDecryptRows", data.messages);
          if (data.messages.some((m) => m.kind === "request" || m.kind === "system")) dispatch("chatApprovals");
          // Anything new in the open channel is read the moment it is really shown.
          if (shown && s.active && data.messages.some((m) => m.channel === s.active)) dispatch("chatMarkRead", s.active);
        }
        if (data.changed && data.changed.length) {
          commit("APPLY_CHAT_CHANGES", data.changed);
          if (data.changed.some((m) => m.kind === "request")) dispatch("chatApprovals");
        }
        commit("SET_CHAT_SYNC", data);
        // Membership moves (a new colleague, a role change): re-read it now and then so keys reach everyone.
        if (s.e2ee.status === "ready" && Date.now() - s.channelsAt > CHANNELS_REFRESH_MS && document.visibilityState === "visible") dispatch("chatRefreshChannels");
      }
    } catch (e) {
      // A missed poll is nothing; the next one catches up.
    } finally { commit("SET_CHAT_INFLIGHT", false); }
    dispatch("chatSchedule");
  },

  /** The channel list again (unread counts, members, public keys), then the keys other people still lack. */
  async chatRefreshChannels({ commit, dispatch, state: s }) {
    try {
      const { data } = await window.$http.axios.get("chat/channels", { silent: true });
      if (!data.status) return;
      const active = s.active;
      commit("SET_CHAT_CHANNELS", data);
      if (active && !s.channels.some((c) => c.key === active) && active.indexOf("dm:") === 0) {
        // A direct message with no rows yet is not in the server's list; keep the open thread.
        const p = active.split(":"); const other = Number(p[1]) === s.me.uid ? Number(p[2]) : Number(p[1]);
        const person = s.people.find((x) => x.uid === other);
        s.channels.push({ key: active, kind: "dm", name: person ? person.name : "User #" + other, other_uid: other, unread: 0, last_read_id: 0 });
      }
      if (s.e2ee.status === "ready") dispatch("e2eeLoadKeys");
    } catch (e) { /* next time */ }
  },

  /** Open a channel: load its history once, mark it read. */
  async chatOpen({ commit, dispatch, state: s }, key) {
    commit("SET_CHAT_ACTIVE", key);
    if (!s.loadedKeys[key]) {
      try {
        const { data } = await window.$http.axios.get("chat/messages", { params: { channel: key } });
        if (data.status) { commit("SET_CHAT_HISTORY", { key, list: data.messages, hasMore: data.has_more }); dispatch("e2eeDecryptRows", data.messages); }
        else notifyError(commit, data.message);
      } catch (e) {
        notifyError(commit, "Could not load the conversation.");
      }
    }
    dispatch("chatMarkRead", key);
    dispatch("chatSchedule");
  },

  async chatLoadMore({ commit, dispatch, state: s }, key) {
    const list = s.messages[key] || [];
    if (!list.length) return;
    try {
      const { data } = await window.$http.axios.get("chat/messages", { params: { channel: key, before: list[0].id } });
      if (data.status) { commit("SET_CHAT_HISTORY", { key, list: data.messages, hasMore: data.has_more, prepend: true }); dispatch("e2eeDecryptRows", data.messages); }
    } catch (e) {
      notifyError(commit, "Could not load earlier messages.");
    }
  },

  async chatMarkRead({ commit, state: s }, key) {
    const list = s.messages[key] || [];
    const last = list.length ? list[list.length - 1].id : 0;
    commit("CHAT_READ_LOCAL", key);
    if (!last) return;
    try { await window.$http.axios.post("chat/read", form({ channel: key, last_id: last })); } catch (e) { /* next poll */ }
  },

  async chatSend({ commit, dispatch, state: s }, { key, body }) {
    let wire = body;
    if (e2eeChannel(s, key)) {
      if (s.e2ee.status !== "ready") { notifyError(commit, "Set up the encryption first (top of the Chat tab)."); return false; }
      if (body.length > PLAIN_MAX) { notifyError(commit, "Keep it under " + PLAIN_MAX.toLocaleString() + " characters."); return false; }
      try {
        const k = await dispatch("e2eeEnsureKey", key);
        wire = await cc.encryptText(k.key, k.ver, body);
      } catch (e) { notifyError(commit, e.message || "Could not encrypt the message."); return false; }
    }
    try {
      const { data } = await window.$http.axios.post("chat/send", form({ channel: key, body: wire }));
      if (data.status) { dispatch("chatPoll"); return data.id || true; }
      notifyError(commit, data.message);
      return false;
    } catch (e) {
      notifyError(commit, "Could not send the message.");
      return false;
    }
  },

  async chatDelete({ commit }, id) {
    try {
      const { data } = await window.$http.axios.post("chat/delete", form({ id }));
      if (data.status) { commit("CHAT_MESSAGE_DELETED", id); return true; }
      notifyError(commit, data.message);
      return false;
    } catch (e) { notifyError(commit, "Could not delete the message."); return false; }
  },

  /** Direct message: derive the key, add the thread to the list if new, open it. */
  chatOpenDm({ state: s, dispatch }, person) {
    const a = Math.min(s.me.uid, person.uid), b = Math.max(s.me.uid, person.uid);
    const key = "dm:" + a + ":" + b;
    if (!s.channels.some((c) => c.key === key)) {
      s.channels.push({ key, kind: "dm", name: person.name, other_uid: person.uid, unread: 0, last_read_id: 0 });
    }
    dispatch("chatOpen", key);
  },

  /** Kept as the name the drawer and the poll use; the list itself lives in approvals.js. */
  chatApprovals({ dispatch }) { return dispatch("loadApprovals"); },

  async chatRequest({ commit, dispatch }, { role, reason }) {
    try {
      const { data } = await window.$http.axios.post("chat/request", form({ role, reason }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Access request", message: "Sent. An administrator will see it in their Approvals." });
        dispatch("chatApprovals");
        return true;
      }
      notifyError(commit, data.message);
      return false;
    } catch (e) { notifyError(commit, "Could not send the request."); return false; }
  },

  async chatResolve({ commit, dispatch }, { id, decision, note }) {
    try {
      const { data } = await window.$http.axios.post("chat/resolve", form({ id, decision, note: note || "" }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Access request", message: decision === "approved" ? "Approved — the role is assigned." : "Declined." });
        dispatch("chatApprovals");
        dispatch("chatPoll");
        return true;
      }
      notifyError(commit, data.message);
      return false;
    } catch (e) { notifyError(commit, "Could not resolve the request."); return false; }
  },

  async chatCreateGroup({ commit, dispatch }, { name, members, allowedRoles }) {
    try {
      const { data } = await window.$http.axios.post("chat/group", form({ name, members: members || [], allowed_roles: allowedRoles || [] }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Chat", message: "Group created." });
        await dispatch("chatRefreshChannels");
        dispatch("chatOpen", data.key);
        return true;
      }
      notifyError(commit, data.message);
      return false;
    } catch (e) { notifyError(commit, "Could not create the group."); return false; }
  },

  /** Archive a group (soft delete: it leaves every list; rows stay for the record). */
  async chatArchiveGroup({ commit }, key) {
    const id = parseInt(String(key || "").replace("grp:", ""), 10);
    if (!id) return false;
    try {
      const { data } = await window.$http.axios.post("chat/group-archive", form({ id }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Chat", message: "Group deleted." });
        commit("SET_CHAT_ACTIVE", "");
        const { data: ch } = await window.$http.axios.get("chat/channels");
        if (ch.status) commit("SET_CHAT_CHANNELS", ch);
        return true;
      }
      notifyError(commit, data.message);
      return false;
    } catch (e) { notifyError(commit, "Could not delete the group."); return false; }
  },

  chatStop({ commit, state: s }) {
    if (s.timer) clearTimeout(s.timer);
    if (window.__chatWake) {
      document.removeEventListener("visibilitychange", window.__chatWake);
      window.removeEventListener("focus", window.__chatWake);
      window.__chatWake = null;
    }
    commit("RESET_CHAT");
  },

  // ------------------------------------------------------------------ end-to-end encryption

  /** After the channel list: do I have a key here, does the server know it, or is one waiting elsewhere? */
  async e2eeInit({ commit, dispatch, state: s }) {
    const me = s.me;
    if (!me) return;
    if (!s.e2ee.enabled) { commit("SET_E2EE", { status: "off" }); return; }
    if (!s.e2ee.supported) { commit("SET_E2EE", { status: "nocrypto" }); return; }
    vault.uid = me.uid;
    const serverPub = me.pubkey || "";
    commit("SET_E2EE", { hasBackup: !!me.backup, pubkey: serverPub, fp: me.fp || "", error: "" });
    let local = null;
    try { local = await cc.loadIdentity(me.uid); } catch (e) { local = null; }
    if (local && (!serverPub || serverPub === local.pubkey)) {
      vault.priv = local.priv;
      if (!serverPub && !(await dispatch("e2eePublish", { pubkey: local.pubkey, backup: "", reset: false }))) { commit("SET_E2EE", { status: "setup" }); return; }
      commit("SET_E2EE", { status: "ready", fp: local.fp, pubkey: local.pubkey });
      dispatch("e2eeLoadKeys");
      return;
    }
    commit("SET_E2EE", { status: serverPub ? "unlock" : "setup" });
  },

  async e2eePublish({ commit }, { pubkey, backup, reset }) {
    try {
      const { data } = await window.$http.axios.post("chat/identity", form({ pubkey, backup: backup || "", reset: reset ? 1 : 0 }));
      if (data.status) return true;
      commit("SET_E2EE", { error: data.message || "The server refused the key." });
      return false;
    } catch (e) { commit("SET_E2EE", { error: "Could not reach the server." }); return false; }
  },

  /** A new key pair for this account: first time, or a reset when the old one is out of reach. */
  async e2eeSetup({ commit, dispatch, state: s }, { passphrase, reset }) {
    commit("SET_E2EE", { busy: true, error: "" });
    try {
      const id = await cc.createIdentity();
      const backup = passphrase ? await cc.makeBackup(id.pkcs8, id.pubkey, passphrase) : "";
      if (!(await dispatch("e2eePublish", { pubkey: id.pubkey, backup, reset: !!reset }))) return false;
      await cc.clearIdentity(s.me.uid);
      await cc.storeIdentity(s.me.uid, id);
      vault.uid = s.me.uid; vault.priv = id.priv; vault.keys = {}; vault.seen = {}; vault.coverage = []; vault.restored = true;
      commit("SET_E2EE", { status: "ready", fp: id.fp, pubkey: id.pubkey, hasBackup: !!backup });
      if (s.me) { s.me.pubkey = id.pubkey; s.me.fp = id.fp; s.me.backup = backup; }
      await dispatch("e2eeLoadKeys");
      return true;
    } catch (e) {
      commit("SET_E2EE", { error: "Could not set up the encryption in this browser." });
      return false;
    } finally { commit("SET_E2EE", { busy: false }); }
  },

  /** The key exists on another browser and was backed up: open the backup with the passphrase. */
  async e2eeUnlock({ commit, dispatch, state: s }, { passphrase }) {
    commit("SET_E2EE", { busy: true, error: "" });
    try {
      const id = await cc.openBackup(s.me.backup, passphrase);
      if (id.pubkey !== s.me.pubkey) { commit("SET_E2EE", { error: "That backup does not match the key on the server. Make a new key instead." }); return false; }
      await cc.clearIdentity(s.me.uid);
      await cc.storeIdentity(s.me.uid, id);
      vault.uid = s.me.uid; vault.priv = id.priv; vault.keys = {}; vault.seen = {}; vault.coverage = []; vault.restored = true;
      commit("SET_E2EE", { status: "ready", fp: id.fp, pubkey: id.pubkey });
      await dispatch("e2eeLoadKeys");
      return true;
    } catch (e) {
      commit("SET_E2EE", { error: "That is not the passphrase." });
      return false;
    } finally { commit("SET_E2EE", { busy: false }); }
  },

  /** Every wrapped key addressed to me, plus who holds what; then wrap for members who lack it and open what waits. */
  async e2eeLoadKeys({ dispatch, state: s }) {
    if (s.e2ee.status !== "ready" || !vault.priv) return;
    if (vault.loading) return vault.loading;
    vault.lastKeyFetch = Date.now();
    vault.loading = (async () => {
      try {
        if (!vault.restored) {
          vault.restored = true;
          for (const k of await cc.loadChannelKeys(s.me.uid)) await holdKey(k.channel, k.ver, k.raw, false);
        }
        const { data } = await window.$http.axios.get("chat/keys", { silent: true });
        if (!data.status) return;
        vault.coverage = data.coverage || [];
        await dispatch("e2eeAbsorbKeyRows", data.mine || []);
        await dispatch("e2eeFillGaps");
        await dispatch("e2eeDecryptAll");
      } catch (e) { /* next poll */ } finally { vault.loading = null; }
    })();
    return vault.loading;
  },

  /** Wrapped-key rows (from /chat/keys or the poll): unwrap the ones for my key. */
  async e2eeAbsorbKeyRows({ state: s }, rows) {
    if (!vault.priv || !rows || !rows.length) return;
    for (const r of rows) {
      if (vault.seen[r.id]) continue;
      vault.seen[r.id] = true;
      if (r.fp !== s.e2ee.fp) continue;                                   // wrapped for a key this browser no longer holds
      if (vault.keys[r.channel] && vault.keys[r.channel][r.ver]) continue;
      try { await holdKey(r.channel, r.ver, await cc.unwrapWith(vault.priv, r.body), true); } catch (e) { /* not ours after all */ }
    }
  },

  /** The newest key of a channel; the first one is made here when nobody has written yet. */
  async e2eeEnsureKey({ dispatch }, channel) {
    let ver = newestVer(channel);
    if (!ver) { await dispatch("e2eeLoadKeys"); ver = newestVer(channel); }
    if (ver) return { ver, key: vault.keys[channel][ver].key };
    return dispatch("e2eeCreateKey", { channel, ver: 1 });
  },

  /**
   * Make version `ver` of a channel's key and wrap it for every member who has a public key (me
   * included). When that version exists but nobody has shared it with this browser yet (a new
   * member, a new key), the next version is made instead, so nobody waits to write; the older
   * versions reach them when a holder's panel runs e2eeFillGaps.
   */
  async e2eeCreateKey({ dispatch, state: s }, { channel, ver, retried }) {
    let members = membersOf(s, channel);
    if (!members) { await dispatch("chatRefreshChannels"); members = membersOf(s, channel) || [s.me.uid]; }
    const { raw, key } = await cc.newChannelKey();
    const rows = await wrapRowsFor(s, channel, raw, members);
    const { data } = await window.$http.axios.post("chat/keys", form({ channel, ver, rows: JSON.stringify(rows) }));
    if (data.status) { await holdKey(channel, ver, raw, true); markCovered(channel, ver, rows); return { ver, key }; }
    if ((data.code === "exists" || data.code === "version") && !retried) {
      // Someone else made it: take theirs if it was shared with me, else start the next version.
      vault.loading = null;
      await dispatch("e2eeLoadKeys");
      const v = newestVer(channel);
      if (v) return { ver: v, key: vault.keys[channel][v].key };
      return dispatch("e2eeCreateKey", { channel, ver: (Number(data.max_ver) || ver) + 1, retried: true });
    }
    throw new Error(data.message || "Could not make the channel key.");
  },

  /**
   * For every key version I hold: members without a copy get one (older versions too, so a
   * newcomer can read the history); someone gone from the newest version's readers means a
   * new version for those who remain.
   */
  async e2eeFillGaps({ dispatch, state: s }) {
    if (vault.filling || s.e2ee.status !== "ready") return;
    vault.filling = true;
    try {
      for (const channel of Object.keys(vault.keys)) {
        if (!e2eeChannel(s, channel)) continue;
        const members = membersOf(s, channel);
        const newest = newestVer(channel);
        if (!newest || !members) continue;
        for (const ver of Object.keys(vault.keys[channel]).map(Number).sort((a, b) => a - b)) {
          const cov = vault.coverage.filter((c) => c.channel === channel && c.ver === ver);
          if (ver === newest && cov.some((c) => members.indexOf(c.uid) === -1)) { await dispatch("e2eeCreateKey", { channel, ver: ver + 1 }); break; }
          const covered = {}; cov.forEach((c) => { covered[c.uid + ":" + c.fp] = true; });
          const missing = members.filter((uid) => { const p = pubOf(s, uid); return p && !covered[uid + ":" + p.fp]; });
          if (!missing.length) continue;
          const rows = await wrapRowsFor(s, channel, vault.keys[channel][ver].raw, missing);
          if (!rows.length) continue;
          const { data } = await window.$http.axios.post("chat/keys", form({ channel, ver, rows: JSON.stringify(rows) }), { silent: true });
          if (data.status) markCovered(channel, ver, rows);
        }
      }
    } catch (e) { /* the next refresh tries again */ } finally { vault.filling = false; }
  },

  /** Decrypt what this browser can; lock the rest and ask for keys (at most every KEYS_RETRY_MS). */
  async e2eeDecryptRows({ commit, dispatch, state: s }, list) {
    if (!list || !list.length || !s.e2ee.enabled) return;
    const out = []; let unknown = false;
    for (const m of list) {
      if (m.kind !== "text" || m.deleted) continue;
      const e = cc.parseEnc(m.body);
      if (!e) continue;
      const k = vault.keys[m.channel] && vault.keys[m.channel][e.ver];
      if (!k) { unknown = true; out.push({ channel: m.channel, id: m.id, text: null, locked: true }); continue; }
      try { out.push({ channel: m.channel, id: m.id, text: await cc.decryptText(k.key, m.body), locked: false }); }
      catch (err) { out.push({ channel: m.channel, id: m.id, text: null, locked: true }); }
    }
    if (out.length) commit("CHAT_SET_TEXTS", out);
    if (unknown && s.e2ee.status === "ready" && !vault.loading && Date.now() - vault.lastKeyFetch > KEYS_RETRY_MS) dispatch("e2eeLoadKeys");
  },

  /** Every encrypted row not yet opened (after new keys arrived). */
  e2eeDecryptAll({ dispatch, state: s }) {
    const all = [];
    Object.keys(s.messages).forEach((k) => (s.messages[k] || []).forEach((m) => { if (m.locked || (m.text === undefined && cc.isEncrypted(m.body))) all.push(m); }));
    return all.length ? dispatch("e2eeDecryptRows", all) : null;
  },
};

export default { state, getters, mutations, actions };
